import React, { useEffect, useMemo, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  ChevronDown,
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
  Printer,
  Table2,
} from "lucide-react";
import { toast } from "sonner";
import { api, extractError } from "../lib/api";
import { downloadApiFile, openApiPrintView } from "../lib/downloadApiFile";

const VOUCHER_TYPE_OPTIONS = [
  { value: "MATERIAL", label: "Purchases (Material)" },
  { value: "FREIGHT", label: "Freight / expense" },
  { value: "TDS", label: "TDS deductions" },
  { value: "CASH", label: "Cash / bank payments" },
  { value: "RECEIPT", label: "Receipts" },
  { value: "SALES", label: "Sales invoices" },
];

const FORMAT_OPTIONS = [
  {
    id: "excel",
    label: "Excel (.xlsx)",
    hint: "Ledger sheet + type summary (+ party cover when Detailed)",
    icon: FileSpreadsheet,
  },
  {
    id: "pdf",
    label: "PDF statement",
    hint: "Branded printable PDF (landscape when Detailed)",
    icon: FileText,
  },
  {
    id: "csv",
    label: "CSV (.csv)",
    hint: "Flat file for Excel / Tally import prep",
    icon: Table2,
  },
  {
    id: "html",
    label: "Print preview",
    hint: "Open HTML statement ready to print / PDF-save",
    icon: Printer,
  },
];

function buildFilename(partyName, format, detail) {
  const safe = String(partyName || "ledger")
    .replace(/[^\w\-]+/g, "_")
    .slice(0, 40);
  const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  const ext = format === "excel" ? "xlsx" : format === "html" ? "html" : format;
  return `ledger-${safe}-${detail}-${stamp}.${ext}`;
}

export default function PartyLedgerExportDialog({
  ledgerId = "",
  partyId,
  partyName = "",
  startDate = "",
  endDate = "",
  includeDrafts = false,
  disabled = false,
  testId = "party-ledger-export",
}) {
  const resolvedLedgerId = ledgerId || (partyId ? `party:${partyId}` : "");
  const isPartyLedger = String(resolvedLedgerId).startsWith("party:");

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [format, setFormat] = useState("excel");
  const [detail, setDetail] = useState("detailed");
  const [fromDate, setFromDate] = useState(startDate);
  const [toDate, setToDate] = useState(endDate);
  const [drafts, setDrafts] = useState(includeDrafts);
  const [types, setTypes] = useState(() => VOUCHER_TYPE_OPTIONS.map((o) => o.value));

  useEffect(() => {
    if (!open) return;
    setFromDate(startDate || "");
    setToDate(endDate || "");
    setDrafts(!!includeDrafts);
    setTypes(VOUCHER_TYPE_OPTIONS.map((o) => o.value));
    setFormat("excel");
    setDetail("detailed");
  }, [open, startDate, endDate, includeDrafts]);

  const allTypesSelected = types.length === VOUCHER_TYPE_OPTIONS.length;
  const typeParam = useMemo(() => {
    if (!isPartyLedger) return undefined;
    if (!types.length || allTypesSelected) return undefined;
    return types.join(",");
  }, [types, allTypesSelected, isPartyLedger]);

  const toggleType = (value) => {
    setTypes((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const exportParams = () => ({
    format,
    detail,
    start_date: fromDate || undefined,
    end_date: toDate || undefined,
    include_drafts: drafts,
    ...(typeParam ? { voucher_types: typeParam } : {}),
  });

  const runExport = async (override = {}) => {
    if (!resolvedLedgerId) {
      toast.error("Select a ledger first");
      return;
    }
    if (fromDate && toDate && fromDate > toDate) {
      toast.error("From date must be on or before To date");
      return;
    }
    if (isPartyLedger && !types.length) {
      toast.error("Select at least one voucher type");
      return;
    }
    const fmt = override.format || format;
    const det = override.detail || detail;
    const key = `${fmt}-${det}`;
    setBusy(key);
    try {
      const params = {
        ...exportParams(),
        format: fmt,
        detail: det,
        ...(override.autoprint ? { autoprint: 1 } : {}),
      };
      const path = `/accounts/ledgers/${resolvedLedgerId}/statement/export`;
      if (fmt === "html") {
        if (override.autoprint) {
          await openApiPrintView(path, params);
          toast.success("Print dialog opened");
        } else {
          const { data } = await api.get(path, { params, responseType: "text" });
          const win = window.open("", "_blank");
          if (!win) throw new Error("Pop-up blocked. Allow pop-ups to open print preview.");
          win.document.write(data);
          win.document.close();
          win.focus();
          toast.success("Print preview opened");
        }
      } else {
        await downloadApiFile(path, buildFilename(partyName, fmt, det), params);
        toast.success(
          `${fmt === "excel" ? "Excel" : fmt === "pdf" ? "PDF" : "CSV"} download started`
        );
      }
      setOpen(false);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy("");
    }
  };

  const quickBusy = !!busy && !open;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testId}>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant="default"
            disabled={disabled || !resolvedLedgerId || quickBusy}
            data-testid={`${testId}-open`}
          >
            {busy && open ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-1" />
            )}
            Download / Export
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle>Download ledger statement</DialogTitle>
            <DialogDescription>
              Choose format, detail level, and period. Detailed Excel includes ledger sheet,
              type summary, and cover details.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            <div>
              <Label className="text-xs text-muted-foreground">Ledger</Label>
              <p className="text-sm font-medium mt-0.5">
                {partyName || resolvedLedgerId || "—"}
              </p>
              {resolvedLedgerId ? (
                <p className="text-[11px] text-muted-foreground font-mono mt-0.5">{resolvedLedgerId}</p>
              ) : null}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">From date</Label>
                <Input
                  type="date"
                  className="mt-1 h-9"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">To date</Label>
                <Input
                  type="date"
                  className="mt-1 h-9"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Layout</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                {[
                  { id: "detailed", label: "Detailed", hint: "Ref, mode, tally, notes + summary" },
                  { id: "compact", label: "Compact", hint: "Tally-style 7 columns only" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setDetail(opt.id)}
                    className={`rounded-md border px-3 py-2 text-left transition-colors ${
                      detail === opt.id
                        ? "border-primary bg-primary/10"
                        : "border-border bg-card hover:bg-muted/40"
                    }`}
                  >
                    <div className="text-sm font-medium">{opt.label}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{opt.hint}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs">Format</Label>
              <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {FORMAT_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setFormat(opt.id)}
                      className={`rounded-md border px-3 py-2 text-left transition-colors ${
                        format === opt.id
                          ? "border-primary bg-primary/10"
                          : "border-border bg-card hover:bg-muted/40"
                      }`}
                    >
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Icon className="h-3.5 w-3.5 shrink-0" />
                        {opt.label}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">{opt.hint}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {isPartyLedger ? (
              <div>
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs">Voucher types</Label>
                  <button
                    type="button"
                    className="text-[11px] text-primary hover:underline"
                    onClick={() =>
                      setTypes(
                        allTypesSelected ? [] : VOUCHER_TYPE_OPTIONS.map((o) => o.value)
                      )
                    }
                  >
                    {allTypesSelected ? "Clear all" : "Select all"}
                  </button>
                </div>
                <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {VOUCHER_TYPE_OPTIONS.map((opt) => {
                    const checked = types.includes(opt.value);
                    return (
                      <label
                        key={opt.value}
                        className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-xs cursor-pointer hover:bg-muted/40"
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleType(opt.value)}
                        />
                        {opt.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            ) : null}

            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <Checkbox checked={drafts} onCheckedChange={(v) => setDrafts(!!v)} />
              Include draft vouchers
            </label>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={!!busy}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => runExport()}
              disabled={!!busy || !resolvedLedgerId}
              data-testid={`${testId}-confirm`}
            >
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
              {format === "html" ? "Open print view" : "Download"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={disabled || !resolvedLedgerId || !!busy}
            data-testid={`${testId}-quick`}
          >
            {busy && !open ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            Quick download
            <ChevronDown className="h-3.5 w-3.5 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={() => runExport({ format: "excel", detail: "detailed" })}>
            <FileSpreadsheet className="h-3.5 w-3.5 mr-2" />
            Detailed Excel
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => runExport({ format: "excel", detail: "compact" })}>
            <FileSpreadsheet className="h-3.5 w-3.5 mr-2" />
            Compact Excel
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => runExport({ format: "pdf", detail: "detailed" })}>
            <FileText className="h-3.5 w-3.5 mr-2" />
            Detailed PDF
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => runExport({ format: "pdf", detail: "compact" })}>
            <FileText className="h-3.5 w-3.5 mr-2" />
            Compact PDF
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => runExport({ format: "csv", detail: "detailed" })}>
            <Table2 className="h-3.5 w-3.5 mr-2" />
            Detailed CSV
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => runExport({ format: "html", detail: "detailed", autoprint: true })}
          >
            <Printer className="h-3.5 w-3.5 mr-2" />
            Print statement
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
